import type { LayerGroup, Marker, Polygon } from 'leaflet';
import { recomputeParking, recomputeSlopeCheck, scheduleSave, state } from './app';
import { redrawBuildingsOverlay } from './buildingRenderer';
import {
  addP,
  latLngToLocal,
  localToLatLng,
  polygonArea,
  projectPointOnSegment,
  scaleVec,
  subP,
} from './geometry';
import { getLeaflet, map } from './mapView';
import { PARKING_METHOD_KEYS } from './parking/constants';
import { defaultState } from './state';
import type { LatLng, SiteEdge } from './types';
import { renderSidebar } from './ui/sidebar';

// Site polygon editing via Leaflet-native layers (draft vertices during
// site-edit mode, the locked/confirmed site polygon, and driveway markers).

let lockedSiteLayer: LayerGroup | null = null;
let siteDraftLatLngs: LatLng[] = [];
let siteDraftMarkers: Marker[] = [];
let siteDraftPolygon: Polygon | null = null;

function numberedDivIcon(n: number) {
  const L = getLeaflet();
  return L.divIcon({
    className: '',
    html: `<div style="width:22px;height:22px;border-radius:50%;background:#fff;border:2px solid var(--accent);
      color:var(--accent-dark);font-weight:800;font-size:11px;display:flex;align-items:center;justify-content:center;
      box-shadow:0 1px 3px rgba(0,0,0,.35);">${n}</div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

function edgeNumberDivIcon(n: number) {
  const L = getLeaflet();
  return L.divIcon({
    className: '',
    html: `<div style="min-width:18px;height:18px;padding:0 3px;border-radius:9px;background:#374151;
      color:#fff;font-weight:700;font-size:10px;display:flex;align-items:center;justify-content:center;
      box-shadow:0 1px 3px rgba(0,0,0,.35);">${n}</div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
}
export function drawLockedSitePolygon(): void {
  const L = getLeaflet();
  if (lockedSiteLayer) {
    map?.removeLayer(lockedSiteLayer);
    lockedSiteLayer = null;
  }
  if (state.site.latlngs.length < 3) return;
  const group = L.layerGroup();
  L.polygon(
    state.site.latlngs.map((ll) => ({ lat: ll.lat, lng: ll.lng })),
    { color: '#1a1a1a', weight: 2.5, fillColor: '#8b9096', fillOpacity: 0.35, interactive: false }
  ).addTo(group);
  const edges = state.site.edges || [];
  for (let i = 0; i < state.site.latlngs.length; i++) {
    const p1 = state.site.latlngs[i];
    const p2 = state.site.latlngs[(i + 1) % state.site.latlngs.length];
    const edge: Pick<SiteEdge, 'type'> = edges[i] || { type: 'none' };
    let color: string | null = null;
    let dash: string | null = null;
    const weight = 3;
    if (edge.type === 'road') {
      color = '#f5b400';
    } else if (edge.type === 'neighbor') {
      color = '#2f9e44';
      dash = '6 4';
    }
    if (color) {
      L.polyline(
        [
          [p1.lat, p1.lng],
          [p2.lat, p2.lng],
        ],
        { color, weight, dashArray: dash ?? undefined, interactive: false, pane: 'siteEdgePane' }
      ).addTo(group);
    }
    const midLat = (p1.lat + p2.lat) / 2;
    const midLng = (p1.lng + p2.lng) / 2;
    L.marker([midLat, midLng], { icon: edgeNumberDivIcon(i + 1), interactive: false }).addTo(group);
  }
  lockedSiteLayer = group.addTo(map as NonNullable<typeof map>);
  rebuildDrivewayMarkers();
}

function drivewayDivIcon() {
  const L = getLeaflet();
  return L.divIcon({
    className: '',
    html: `<div style="width:18px;height:18px;border-radius:4px;background:#f5b400;border:2px solid #a56800;
      display:flex;align-items:center;justify-content:center;font-size:10px;box-shadow:0 1px 3px rgba(0,0,0,.35);">🚗</div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
}
let drivewayMarkers: Marker[] = [];
export function rebuildDrivewayMarkers(): void {
  drivewayMarkers.forEach((m) => {
    map?.removeLayer(m);
  });
  drivewayMarkers = [];
  if (!state.site.locked || state.ui.mode === 'site-edit' || !state.site.origin) return;
  const L = getLeaflet();
  const origin = state.site.origin;
  state.site.edges.forEach((edge, i) => {
    if (edge.type !== 'road' || !Array.isArray(edge.driveways)) return;
    const p1 = state.site.points[i];
    const p2 = state.site.points[(i + 1) % state.site.points.length];
    edge.driveways.forEach((dw) => {
      const localPt = addP(p1, scaleVec(subP(p2, p1), dw.position));
      const ll = localToLatLng(localPt, origin);
      const marker = L.marker([ll.lat, ll.lng], { icon: drivewayDivIcon(), draggable: true });
      // biome-ignore lint/suspicious/noExplicitAny: Leaflet drag-event payload
      marker.on('drag', (e: any) => {
        const p = e.target.getLatLng();
        const localDragPt = latLngToLocal({ lat: p.lat, lng: p.lng }, origin);
        const proj = projectPointOnSegment(localDragPt, p1, p2);
        dw.position = proj.t;
        const snappedLL = localToLatLng(proj.point, origin);
        marker.setLatLng([snappedLL.lat, snappedLL.lng]);
      });
      marker.on('dragend', () => {
        recomputeParking();
        renderSidebar();
        redrawBuildingsOverlay();
        scheduleSave();
      });
      marker.on('dblclick', () => {
        if (totalDrivewayCount() <= 1) return;
        edge.driveways = edge.driveways.filter((x) => x.id !== dw.id);
        rebuildDrivewayMarkers();
        recomputeParking();
        redrawBuildingsOverlay();
        renderSidebar();
        scheduleSave();
      });
      marker.addTo(map as NonNullable<typeof map>);
      drivewayMarkers.push(marker);
    });
  });
}
export function totalDrivewayCount(): number {
  return (state.site.edges || []).reduce(
    (sum, e) => sum + (Array.isArray(e.driveways) ? e.driveways.length : 0),
    0
  );
}

export function clearSiteDraftLayers(): void {
  siteDraftMarkers.forEach((m) => {
    map?.removeLayer(m);
  });
  siteDraftMarkers = [];
  if (siteDraftPolygon) {
    map?.removeLayer(siteDraftPolygon);
    siteDraftPolygon = null;
  }
}

export function refreshSiteDraftPolygon(): void {
  const L = getLeaflet();
  if (siteDraftPolygon) {
    map?.removeLayer(siteDraftPolygon);
    siteDraftPolygon = null;
  }
  if (siteDraftLatLngs.length >= 2) {
    siteDraftPolygon = L.polygon(
      siteDraftLatLngs.map((ll) => ({ lat: ll.lat, lng: ll.lng })),
      {
        color: 'var(--accent-dark)',
        weight: 2,
        fillColor: '#ffe4d5',
        fillOpacity: 0.35,
        interactive: false,
        dashArray: '4 4',
      }
    ).addTo(map as NonNullable<typeof map>);
  }
  updateSiteStatusBar();
}

export function rebuildSiteDraftMarkers(): void {
  const L = getLeaflet();
  siteDraftMarkers.forEach((m) => {
    map?.removeLayer(m);
  });
  siteDraftMarkers = siteDraftLatLngs.map((ll, i) => {
    const marker = L.marker([ll.lat, ll.lng], { icon: numberedDivIcon(i + 1), draggable: true });
    // biome-ignore lint/suspicious/noExplicitAny: Leaflet drag-event payload
    marker.on('drag', (e: any) => {
      const p = e.target.getLatLng();
      siteDraftLatLngs[i] = { lat: p.lat, lng: p.lng };
      refreshSiteDraftPolygon();
    });
    marker.on('dblclick', () => {
      if (siteDraftLatLngs.length <= 3) return;
      siteDraftLatLngs.splice(i, 1);
      rebuildSiteDraftMarkers();
      refreshSiteDraftPolygon();
    });
    marker.addTo(map as NonNullable<typeof map>);
    return marker;
  });
}

export function enterSiteEditMode(): void {
  state.ui.mode = 'site-edit';
  state.ui.selectedId = null;
  siteDraftLatLngs = state.site.locked ? state.site.latlngs.map((ll) => ({ ...ll })) : [];
  if (lockedSiteLayer) {
    map?.removeLayer(lockedSiteLayer);
    lockedSiteLayer = null;
  }
  drivewayMarkers.forEach((m) => {
    map?.removeLayer(m);
  });
  drivewayMarkers = [];
  rebuildSiteDraftMarkers();
  refreshSiteDraftPolygon();
  redrawBuildingsOverlay();
  updateToolbar();
}

export function exitSiteEditMode(): void {
  state.ui.mode = 'view';
  clearSiteDraftLayers();
  const bar = document.getElementById('map-status-bar');
  if (bar) bar.hidden = true;
  updateToolbar();
  redrawBuildingsOverlay();
}

// biome-ignore lint/suspicious/noExplicitAny: Leaflet map click event
export function onMapClick(e: any): void {
  if (state.ui.mode !== 'site-edit') return;
  siteDraftLatLngs.push({ lat: e.latlng.lat, lng: e.latlng.lng });
  rebuildSiteDraftMarkers();
  refreshSiteDraftPolygon();
}

export function updateSiteStatusBar(): void {
  const bar = document.getElementById('map-status-bar');
  if (!bar) return;
  if (state.ui.mode !== 'site-edit') {
    bar.hidden = true;
    return;
  }
  bar.hidden = false;
  const count = siteDraftLatLngs.length;
  let areaText = '—';
  if (count >= 3) {
    const origin = siteDraftLatLngs[0];
    const pts = siteDraftLatLngs.map((ll) => latLngToLocal(ll, origin));
    areaText = `${Math.abs(polygonArea(pts)).toFixed(1)} m²`;
  }
  const statusText = document.getElementById('site-status-text');
  if (statusText) statusText.textContent = `頂点数: ${count} / 概算面積: ${areaText}`;
  const confirmBtn = document.getElementById('site-confirm-btn') as HTMLButtonElement | null;
  if (confirmBtn) confirmBtn.disabled = count < 3;
}

export function siteUndoVertex(): void {
  siteDraftLatLngs.pop();
  rebuildSiteDraftMarkers();
  refreshSiteDraftPolygon();
}
export function siteClearDraft(): void {
  siteDraftLatLngs = [];
  rebuildSiteDraftMarkers();
  refreshSiteDraftPolygon();
}
export function siteCancelEdit(): void {
  exitSiteEditMode();
  if (state.site.locked) drawLockedSitePolygon();
}
export function siteConfirm(): void {
  if (siteDraftLatLngs.length < 3) return;
  const hadBuildings = state.buildings.length > 0;
  const originChanged =
    !state.site.origin ||
    state.site.origin.lat !== siteDraftLatLngs[0].lat ||
    state.site.origin.lng !== siteDraftLatLngs[0].lng;
  if (hadBuildings && originChanged) {
    const ok = confirm(
      '敷地の基準点が変わるため、既存のマンション棟はすべて削除されます。よろしいですか?'
    );
    if (!ok) return;
    state.buildings = [];
  }
  const origin = siteDraftLatLngs[0];
  state.site.origin = origin;
  state.site.latlngs = siteDraftLatLngs.slice();
  state.site.points = siteDraftLatLngs.map((ll) => latLngToLocal(ll, origin));
  if (!Array.isArray(state.site.edges) || state.site.edges.length !== siteDraftLatLngs.length) {
    state.site.edges = siteDraftLatLngs.map(() => ({
      type: 'none' as const,
      roadWidthM: null,
      driveways: [],
    }));
  }
  state.site.locked = true;
  clearSiteDraftLayers();
  exitSiteEditMode();
  drawLockedSitePolygon();
  recomputeSlopeCheck();
  recomputeParking();
  redrawBuildingsOverlay();
  renderSidebar();
  scheduleSave();
}
export function showConfirmDialog(message: string, onConfirm: () => void): void {
  const modal = document.getElementById('confirm-modal');
  const messageEl = document.getElementById('confirm-modal-message');
  const okBtn = document.getElementById('confirm-modal-ok');
  const cancelBtn = document.getElementById('confirm-modal-cancel');
  if (!modal || !messageEl || !okBtn || !cancelBtn) return;
  messageEl.textContent = message;
  const cleanup = () => {
    modal.hidden = true;
    okBtn.removeEventListener('click', onOk);
    cancelBtn.removeEventListener('click', onCancel);
  };
  const onOk = () => {
    cleanup();
    onConfirm();
  };
  const onCancel = () => {
    cleanup();
  };
  okBtn.addEventListener('click', onOk);
  cancelBtn.addEventListener('click', onCancel);
  modal.hidden = false;
}
export function siteClearAll(): void {
  showConfirmDialog('敷地とすべてのマンション棟をクリアします。よろしいですか?', () => {
    if (lockedSiteLayer) {
      map?.removeLayer(lockedSiteLayer);
      lockedSiteLayer = null;
    }
    drivewayMarkers.forEach((m) => {
      map?.removeLayer(m);
    });
    drivewayMarkers = [];
    state.site = defaultState().site;
    state.buildings = [];
    state.ui.selectedId = null;
    PARKING_METHOD_KEYS.forEach((key) => {
      state.parking.methods[key].zone = null;
    });
    state.bicycle.zone = null;
    state.bicycle.autoZone = true;
    recomputeSlopeCheck();
    recomputeParking();
    redrawBuildingsOverlay();
    renderSidebar();
    updateToolbar();
    scheduleSave();
  });
}

export function updateToolbar(): void {
  const editBtn = document.getElementById('btn-site-edit') as HTMLButtonElement | null;
  const clearBtn = document.getElementById('btn-site-clear') as HTMLButtonElement | null;
  const hint = document.getElementById('site-hint');
  if (state.ui.mode === 'site-edit') {
    if (editBtn) editBtn.hidden = true;
    if (clearBtn) clearBtn.hidden = true;
  } else {
    if (editBtn) {
      editBtn.hidden = false;
      editBtn.textContent = state.site.locked ? '✎ 敷地を編集' : '📍 敷地を指定';
    }
    if (clearBtn) clearBtn.hidden = !state.site.locked;
  }
  if (hint) hint.hidden = state.site.locked || state.ui.mode === 'site-edit';
}
